import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCategories, useColors, useSizeGuides, useMaterials } from '../../../features/products/hooks/useMetaData'
import { productsApi } from '../../../features/products/api/productsApi'
import type { PropertyData, FormData as ProductFormData } from '../types'
import { transformToApiFormat } from '../utils/dataTransform'

export const useAddProduct = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: categoriesData } = useCategories()
  const { data: colorsData } = useColors()
  const { data: sizeGuideData } = useSizeGuides()
  const { data: materialsData } = useMaterials()

  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    category: '',
    details: '',
    price: '',
    baseCostPrice: '',
    productionTimeInDays: '',
    styleGuide: '',
    quantity: 0,
    images: [],
    discountPercentage: 0,
    media: []
  })

  const [propertyData, setPropertyData] = useState<PropertyData>({
    sizes: []
  })

  const [selectedSize, setSelectedSize] = useState('')
  const [selectedMaterial, setSelectedMaterial] = useState('')
  const [selectedColor, setSelectedColor] = useState('')
  const [showPropertiesModal, setShowPropertiesModal] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)


  // Validation
  const isBaseDataValid = () => {
    return formData.name && formData.category && formData.details && formData.price
  }

  const canAddVariants = () => {
    return isBaseDataValid()
  }

  // Load/Save localStorage
  useEffect(() => {
    const saved = localStorage.getItem('addProductData')
    if (saved) {
      const data = JSON.parse(saved)
      setFormData(data.formData || formData)
      setPropertyData(data.propertyData || { sizes: [] })
      setShowPropertiesModal(data.showPropertiesModal || false)
      setCurrentStep(data.currentStep || 0)
      setSelectedSize(data.selectedSize || '')
      setSelectedMaterial(data.selectedMaterial || '')
      setSelectedColor(data.selectedColor || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const dataToSave = {
      formData, propertyData, showPropertiesModal, currentStep,
      selectedSize, selectedMaterial, selectedColor
    }
    localStorage.setItem('addProductData', JSON.stringify(dataToSave))
  }, [formData, propertyData, showPropertiesModal, currentStep, selectedSize, selectedMaterial, selectedColor])

  // Mutations
  const createDraftMutation = useMutation({
    mutationFn: (data: FormData) => productsApi.drafts.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'drafts'] })
      localStorage.removeItem('addProductData')
      navigate('/products')
    }
  })

  const createProductMutation = useMutation({
    mutationFn: (data: FormData) => productsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', 'drafts'] })
      localStorage.removeItem('addProductData')
      navigate('/products')
    }
  })

  // Actions
  const handleSaveDraft = async () => {
    if (!isBaseDataValid()) return
    
    const transformedData = transformToApiFormat(formData, propertyData, {
      categoriesData, colorsData, sizeGuideData, materialsData
    }, 'DRAFT')
    createDraftMutation.mutate(transformedData)
  }

  const handlePublish = async () => {
    if (!isBaseDataValid()) return
    
    const transformedData = transformToApiFormat(formData, propertyData, {
      categoriesData, colorsData, sizeGuideData, materialsData
    }, 'PUBLISHED')
    createProductMutation.mutate(transformedData)
  }

  return {
    // Data
    formData, setFormData,
    propertyData, setPropertyData,
    selectedSize, setSelectedSize,
    selectedMaterial, setSelectedMaterial,
    selectedColor, setSelectedColor,
    showPropertiesModal, setShowPropertiesModal,
    currentStep, setCurrentStep,
    
    // API Data
    categories: categoriesData?.data || [],
    apiColors: colorsData?.data || [],
    sizeGuides: sizeGuideData?.result?.data || [],
    materials: materialsData?.data || [],
    
    // Validation
    isBaseDataValid,
    canAddVariants,
    
    // Loading
    isDraftLoading: createDraftMutation.isPending,
    isPublishLoading: createProductMutation.isPending,
    
    // Actions
    handleSaveDraft,
    handlePublish,
    navigate
  }
}